import ExcelJS from "exceljs";
import { AchievementLevelData } from "atoms/Ltool/matrixAtoms";
import { assessmentExcelArgb, resolveAssessmentKey } from "utils/assessment";
import { DRIVE_MODE_IDS, PHENOMENON_IDS, UNIT_IDS } from "utils/constsLtool";
import { DRIVE_MODE_IDS as HEV_DRIVE_MODE_IDS, PHENOMENON_IDS as HEV_PHENOMENON_IDS } from "utils/constsHev";

type FillStyle = NonNullable<ExcelJS.Style["fill"]>;

// 判定の色は画面と同じ定義（utils/assessment）を使う。色なし（未知の値）は塗らない。
const assessmentFill = (value: string | null | undefined): FillStyle | undefined => {
    const argb = assessmentExcelArgb(resolveAssessmentKey(value));
    return argb ? { type: "pattern", pattern: "solid", fgColor: { argb } } : undefined;
};

// Blob は jsdom（テスト環境）で中身を読めないため、Workbook の組み立てを分けてテストできるようにする。
export const buildMatrixWorkbook = (
    data: AchievementLevelData,
    isHev = false,
): ExcelJS.Workbook => {
    const phenomenonIds: readonly string[] = isHev ? HEV_PHENOMENON_IDS : PHENOMENON_IDS;
    const driveModeIds: readonly string[] = isHev ? HEV_DRIVE_MODE_IDS : DRIVE_MODE_IDS;
    // BEV / HEV とも unit>condition の 2 階層（HEV は取得時の URL で gear が絞られている）。
    const getEntry = (unitId: string, conditionKey: string) =>
        data.achievement_level?.[unitId]?.[conditionKey];

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("achievement_matrix");

    // ヘッダー行
    const headerRowValues = ["Unit", "Drive Mode", ...phenomenonIds];
    const headerRow = worksheet.addRow(headerRowValues);
    headerRow.font = { bold: true };

    // データ行
    UNIT_IDS.forEach((unitId) => {
        driveModeIds.forEach((driveModeId) => {
            const rowValues: (string | number | null)[] = [unitId, driveModeId];

            phenomenonIds.forEach((phenomenonId) => {
                const conditionKey = `${phenomenonId}-${driveModeId}`;
                const entry = getEntry(unitId, conditionKey);
                const deviation = entry?.deviation_max;

                // GUI 同様に判定（OK / WARNING / NG）は文字で出さず、偏差の数値と塗りつぶし色で表す
                const cellValue =
                    deviation === null || deviation === undefined
                        ? "-"
                        : Number.isFinite(deviation)
                            ? Number(deviation.toFixed(1))
                            : deviation;

                rowValues.push(cellValue);
            });

            const row = worksheet.addRow(rowValues);

            // 3列目以降が判定セル
            phenomenonIds.forEach((phenomenonId, index) => {
                const conditionKey = `${phenomenonId}-${driveModeId}`;
                const entry = getEntry(unitId, conditionKey);
                const fill = assessmentFill(entry?.assessment);

                const cell = row.getCell(3 + index);

                if (fill) {
                    cell.fill = fill;
                }

                // 中央揃え
                cell.alignment = { horizontal: "center", vertical: "middle" };
            });
        });
    });

    // 列幅を少し広めに
    worksheet.columns.forEach((column) => {
        if (!column.width || column.width < 12) {
            column.width = 12;
        }
    });

    return workbook;
};

export const buildMatrixExcelBlob = async (
    data: AchievementLevelData,
    isHev = false,
): Promise<Blob> => {
    const workbook = buildMatrixWorkbook(data, isHev);
    const buffer = await workbook.xlsx.writeBuffer();

    return new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
};
